import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import MonthlyReportPreview from "./Components/MonthlyReport/MonthlyReportPreview";
import { FiDownload, FiPrinter, FiAlertCircle } from "react-icons/fi";
import "./Components/MonthlyReport/MonthlyReport.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function SharedReportView() {
  const { shareToken } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [activeTheme, setActiveTheme] = useState("light");

  useEffect(() => {
    if (shareToken) {
      fetchPublicReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  const fetchPublicReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/public/reports/${shareToken}`);
      setReport(res.data);
    } catch (err) {
      console.error("Error loading shared report:", err);
      setError(err.response?.data?.detail || "Report not found or link has expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API_BASE}/public/reports/${shareToken}/pdf?theme=${activeTheme}`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const themeSuffix = activeTheme === "dark" ? "_Dark" : "_Light";
      link.setAttribute("download", `Search_AI_Visibility_Report_${report?.report_month || "monthly"}${themeSuffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert("Failed to generate PDF. Please use the Print button to save as PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F6F7FA", flexDirection: "column" }}>
        <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}></div>
        <p style={{ marginTop: "16px", color: "#5B6472", fontWeight: 500 }}>Loading Search &amp; AI Visibility Report...</p>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F6F7FA", padding: "20px" }}>
        <div style={{ maxWidth: "480px", textAlign: "center", background: "#FFFFFF", padding: "40px 32px", borderRadius: "16px", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}>
          <FiAlertCircle style={{ fontSize: "48px", color: "#C24545", marginBottom: "16px" }} />
          <h2 style={{ fontSize: "20px", color: "#0B1C33", marginBottom: "8px" }}>Report Unavailable</h2>
          <p style={{ color: "#5B6472", fontSize: "14px", lineHeight: 1.6 }}>{error || "The requested report is not available or has expired."}</p>
        </div>
      </div>
    );
  }

  const isDark = activeTheme === "dark";

  return (
    <div style={{
      minHeight: "100vh",
      background: isDark ? "#0A0E1A" : "#F6F7FA",
      transition: "background 0.25s ease"
    }}>
      {/* Floating Action Bar */}
      <div className="no-print" style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        background: isDark ? "rgba(10, 14, 26, 0.95)" : "rgba(11, 28, 51, 0.95)",
        backdropFilter: "blur(10px)",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        color: "#FFFFFF",
        borderBottom: "1px solid rgba(255, 255, 255, 0.12)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "13.5px", fontWeight: 600 }}>{report.company_name}</span>
          <span style={{ fontSize: "12px", color: "#94A3B8" }}>— Monthly Search &amp; AI Visibility Report ({report.report_month})</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Light / Dark Mode Toggle */}
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "20px",
            padding: "2px",
            border: "1px solid rgba(255, 255, 255, 0.2)"
          }}>
            <button
              type="button"
              onClick={() => setActiveTheme("light")}
              style={{
                background: !isDark ? "#FFFFFF" : "transparent",
                color: !isDark ? "#0B1C33" : "#CBD5E1",
                border: "none",
                borderRadius: "16px",
                padding: "4px 12px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              ☀️ Light
            </button>
            <button
              type="button"
              onClick={() => setActiveTheme("dark")}
              style={{
                background: isDark ? "#2BD1E4" : "transparent",
                color: isDark ? "#0A0E1A" : "#CBD5E1",
                border: "none",
                borderRadius: "16px",
                padding: "4px 12px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              🌙 Dark
            </button>
          </div>

          <button
            onClick={() => window.print()}
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              color: "#FFFFFF",
              padding: "7px 14px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <FiPrinter /> Print
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            style={{
              background: isDark
                ? "linear-gradient(135deg, #2BD1E4 0%, #8E6CF5 100%)"
                : "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              border: "none",
              color: isDark ? "#0A0E1A" : "#FFFFFF",
              padding: "7px 16px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              boxShadow: isDark ? "0 2px 10px rgba(43, 209, 228, 0.3)" : "none"
            }}
          >
            <FiDownload /> {downloadingPdf ? "Exporting..." : `Download PDF (${isDark ? "Dark" : "Light"})`}
          </button>
        </div>
      </div>

      <div style={{ padding: "30px 16px" }}>
        <MonthlyReportPreview report={report} theme={activeTheme} />
      </div>
    </div>
  );
}
